import React from "react";
import {
  CpSelectMultiPills,
  CpSelectMultiPillsProps,
} from "../cp-select-multi-pills.component";
import {
  useController,
  FieldValues,
  Control,
  UseControllerProps,
  FieldPath,
  FieldPathValue,
  RegisterOptions,
} from "react-hook-form";

export type CpSelectMultiPillsFieldProps<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  CpSelectMultiPillsProps<TData, FieldPathValue<TFieldValues, TName>[number]>,
  "value" | "onChange" | "onBlur" | "error"
> & {
  fieldName: TName;
  control: Control<TFieldValues>;
  controllerOptions?: Omit<
    UseControllerProps<TFieldValues, TName>,
    "name" | "control" | "rules"
  >;
  required?: boolean;
  rules?: RegisterOptions<TFieldValues, TName>;
};

export function CpSelectMultiPillsField<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CpSelectMultiPillsFieldProps<TData, TFieldValues, TName>) {
  const {
    fieldName,
    control,
    controllerOptions,
    required,
    rules = {},
    data,
    transformData,
    ...cpInputMultiProps
  } = props;

  const mergedRules: RegisterOptions<TFieldValues, TName> = {
    ...rules,
    validate: {
      ...rules?.validate,
    },
  };
  if (required) {
    mergedRules.validate = {
      ...mergedRules.validate,
      isRequired: (v: unknown) => {
        return (Array.isArray(v) && v.length > 0) || "Field is required";
      },
    };
  }

  const {
    field: { value, onBlur, onChange, ref },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
    rules: mergedRules,
    ...controllerOptions,
  });

  return (
    <CpSelectMultiPills<TData, FieldPathValue<TFieldValues, TName>[number]>
      {...cpInputMultiProps}
      data={data}
      transformData={transformData}
      error={error?.message}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      triggerRef={ref as any}
    />
  );
}
